/**
 * What a date-range SELECTION means, independent of the control that made it.
 *
 * A selection is a named macro ("previous quarter") plus the two explicit
 * bounds a Custom pick uses — and the two are not interchangeable. A macro is
 * a rule that re-answers itself: a report saved as "current month" is a report
 * about whatever month it is opened in, which is the whole reason somebody
 * picks a macro instead of typing two dates. So the macro is what gets STORED,
 * and the window is COMPUTED from it whenever the selection is used.
 *
 * This lives in `@clockwise/shared` rather than beside the widget because
 * three packages have to agree on it and only one of them can import React:
 * `@clockwise/ui` draws the control, `@clockwise/semantic-core` carries the
 * macro on a report spec, and `apps/api` parses that spec back. The week math
 * below this file moved here for exactly the same reason — see the note above
 * `mondayOf` in `iso-date.ts`, and the drift it records.
 *
 * NO DATE LIBRARY, deliberately, matching `iso-date.ts`: these are calendar
 * dates rather than instants, the math is month/quarter/year arithmetic on
 * `YYYY-MM-DD`, and a second date implementation in this package is the thing
 * that produced B_82. `today` is read through LOCAL getters — the month a
 * person means is the month on their own calendar — and everything after that
 * is string arithmetic, so nothing shifts for a caller west of UTC.
 */
import { isValidIsoDate, mondayOf, sundayOf } from './iso-date';

/**
 * The presets a control offers unless it says otherwise. `all_time` and
 * `current_week` are opt-in (see {@link ALL_DATE_RANGE_PRESETS}) so adding
 * them did not change what existing callers show.
 */
export const DATE_RANGE_PRESETS = [
  'current_month',
  'previous_month',
  'current_quarter',
  'previous_quarter',
  'current_year',
  'previous_year',
  'last_12_months',
  'custom',
] as const;

// `all_time` resolves to NO window (see `resolveDateRange`); callers treat it
// as "no date bounds", so its `from`/`to` pickers are hidden.
export type DateRangePreset = (typeof DATE_RANGE_PRESETS)[number] | 'all_time' | 'current_week';

/** Every preset, including the two opt-in ones. Handy for building a zod
 *  enum or validating a value that arrived from a URL / saved report. */
export const ALL_DATE_RANGE_PRESETS = [
  ...DATE_RANGE_PRESETS,
  'all_time',
  'current_week',
] as const satisfies readonly DateRangePreset[];

/** Selected range: a preset plus explicit `from`/`to` used only when
 *  `preset === 'custom'`. */
export interface DateRangeValue {
  preset: DateRangePreset;
  from?: string;
  to?: string;
}

/** Concrete `YYYY-MM-DD` window. */
export interface ResolvedDateRange {
  from: string;
  to: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `today` as the calendar date the caller is living in, `YYYY-MM-DD`. */
function localIso(today: Date): string {
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
}

/** Days in month `m` (1-based) of year `y`. Day 0 of the NEXT month is the
 *  last day of this one, which is also how leap years take care of themselves. */
function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/** The whole of month `m` (1-based), normalising an out-of-range month so
 *  callers can pass `m - 1` / `m + 2` without wrapping the year themselves. */
function monthSpan(y: number, m: number): ResolvedDateRange {
  const year = y + Math.floor((m - 1) / 12);
  const month = ((((m - 1) % 12) + 12) % 12) + 1;
  return {
    from: `${year}-${pad(month)}-01`,
    to: `${year}-${pad(month)}-${pad(daysInMonth(year, month))}`,
  };
}

/** From the start of `a`'s month through the end of `b`'s. */
function span(a: ResolvedDateRange, b: ResolvedDateRange): ResolvedDateRange {
  return { from: a.from, to: b.to };
}

function presetRange(preset: DateRangePreset, today: Date): ResolvedDateRange | null {
  const iso = localIso(today);
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  // The first month of the quarter `m` falls in, 1-based.
  const q = Math.floor((m - 1) / 3) * 3 + 1;

  switch (preset) {
    case 'current_month':
      return monthSpan(y, m);
    case 'previous_month':
      return monthSpan(y, m - 1);
    case 'current_quarter':
      return span(monthSpan(y, q), monthSpan(y, q + 2));
    case 'previous_quarter':
      return span(monthSpan(y, q - 3), monthSpan(y, q - 1));
    case 'current_year':
      return span(monthSpan(y, 1), monthSpan(y, 12));
    case 'previous_year':
      return span(monthSpan(y - 1, 1), monthSpan(y - 1, 12));
    case 'last_12_months':
      // ROLLING, and ending with the month in progress: eleven whole months
      // behind it plus this one, so a trend reads up to today and every
      // column but the last is a closed month. Whole months at both ends —
      // never "a year ago today" — because the reports that offer this bucket
      // by month, and a window starting mid-month cuts its first bucket short.
      return span(monthSpan(y, m - 11), monthSpan(y, m));
    case 'current_week': {
      // Reuses the shared week-math helpers (Monday start, local-day `today`)
      // instead of a parallel computation, so this preset always resolves to
      // the exact same window as the Timesheets/Approvals week-mode "Current
      // Week" navigation.
      const monday = mondayOf(iso);
      return { from: monday, to: sundayOf(monday) };
    }
    default:
      return null;
  }
}

/**
 * Resolve a selection to a concrete `{ from, to }`. Presets compute relative
 * to `today` (so a saved `previous_month` always means "the month before
 * today"); Custom uses the explicit dates and returns `null` when they're
 * missing or inverted, which callers use to gate "Run".
 */
export function resolveDateRange(
  value: DateRangeValue,
  today: Date = new Date(),
): ResolvedDateRange | null {
  if (value.preset !== 'custom') return presetRange(value.preset, today);
  const { from, to } = value;
  if (!from || !to || !isValidIsoDate(from) || !isValidIsoDate(to) || to < from) return null;
  return { from, to };
}
